/**
 * «منظومة الانخراط» — `GET /api/v3/org/engagement` (integration guide §4).
 *
 * ONE GET builds the whole tab: challenges between units, the streak, the four
 * badges, and the weekly pulse. Two of the three writes (`join`, `pulse`) answer
 * with a DIFFERENT, partial shape (trap 8), so those are modelled separately
 * below — {@link OrgEngagementOverview} and {@link OrgPulse} — and merged
 * field-wise rather than assigned over the whole state.
 */

/** Where a challenge's number comes from. Open-ended: a fourth source must not need a release. */
export type OrgChallengeSource = 'health' | 'khoyek_tool' | 'self_report';

/** `daily` targets are per-day; anything else is cumulative. */
export type OrgChallengeCadence = 'daily' | 'cumulative';

/** Documented metrics. `water_liters` is manual-only — no device feeds it (§5.1). */
export type OrgChallengeMetric =
  | 'steps'
  | 'sleep_minutes'
  | 'mindful_minutes'
  | 'active_minutes'
  | 'water_liters';

export interface OrgLeaderboardRow {
  unitId: number;
  name: string;
  value: number;
  participants: number;
  /** Marks the caller's own unit — the row the design highlights. */
  isMine: boolean;
  rank: number;
}

export interface OrgChallenge {
  id: number;
  key: string;
  title: string;
  tagline: string;
  metric: string;
  source: string;
  /**
   * A TRUST MARK the design prints («تطبيق الصحة على جوالك» · «أدوات خويك» ·
   * «إدخالك اليومي»). Show it — the member should know which kind of number they
   * are looking at.
   */
  sourceLabel: string;
  selfReport: boolean;
  /**
   * Open the manual input on THIS, not on {@link source} (§4.2). The server
   * computes it as `self_report && joined && status == 'active'`, so a fourth
   * source later needs no app release.
   */
  canReport: boolean;
  /** The per-day cap. Exceeding it is REJECTED, not clamped (trap 10). Null = uncapped. */
  maxDailyValue: number | null;
  /** Per-day for `daily`, the total otherwise. */
  target: number;
  unitLabel: string;
  metricLabel: string;
  cadence: string;
  /** Today's value for a `daily` challenge, the running total otherwise. */
  progress: number;
  /** ALWAYS today's stored number — pre-fill a `set` input with this (§4.2). */
  todayValue: number;
  /** `progress / target`, capped at 100; 0 when `target` is 0. */
  percent: number;
  daysLeft: number;
  joined: boolean;
  completed: boolean;
  /** PEOPLE in the challenge, not units. */
  participants: number;
  myUnitRank: number | null;
  /** Only units WITH participants appear — an empty unit is absent, not last. */
  leaderboard: readonly OrgLeaderboardRow[];
  icon: string;
  hue: string;
}

export interface OrgStreak {
  days: number;
  /** Ready to render; `days === 0` gives «ابدأ سلسلتك اليوم». */
  label: string;
}

/** Three states, not a boolean (§4.1). */
export type OrgBadgeState = 'earned' | 'in_progress' | 'locked';

export interface OrgBadge {
  key: string;
  label: string;
  state: OrgBadgeState;
  progress: number;
  target: number;
  note: string;
}

export interface OrgPulseOption {
  /** 1–4; matches what `POST /engagement/pulse` accepts. */
  value: number;
  label: string;
  hue: string;
}

export interface OrgPulseQuestion {
  id: number;
  text: string;
  /** Falls back server-side to four defaults when the org set none. */
  options: readonly OrgPulseOption[];
  /** The caller's current answer, or null. Re-answering before the week ends OVERWRITES. */
  myValue: number | null;
  note: string;
}

export interface OrgPulse {
  /** ISO week (`YYYY-Www`) — ALWAYS present, even when there is no question. */
  period: string;
  answered: boolean;
  /** NULL when the org has no active question, or for a non-member — check before drawing. */
  question: OrgPulseQuestion | null;
}

export interface OrgEngagement {
  challenges: readonly OrgChallenge[];
  streak: OrgStreak;
  /** Always exactly four, always in this order. */
  badges: readonly OrgBadge[];
  pulse: OrgPulse;
}

export const EMPTY_ORG_ENGAGEMENT: OrgEngagement = {
  challenges: [],
  streak: { days: 0, label: '' },
  badges: [],
  pulse: { period: '', answered: false, question: null },
};

/**
 * The PARTIAL payload `POST /challenges/{id}/join` answers with (§4.3) — no
 * badges, no pulse, and `streak_days` a bare int rather than `{days,label}`.
 * Kept as its own type precisely so it can never be fed into {@link OrgEngagement}
 * by accident.
 */
export interface OrgEngagementOverview {
  unitId: number | null;
  streakDays: number;
  challenges: readonly OrgChallenge[];
}

/** `POST /challenges/{id}/progress` success (§4.4). */
export interface OrgProgressResult {
  /** The stored value after the write. */
  value: number;
  /** The single updated card. */
  challenge: OrgChallenge | null;
  overview: OrgEngagementOverview;
}

/**
 * How a self-report input writes. The whole app uses `set` — the input shows the
 * current number (pre-filled from `today_value`), which is exactly the shape
 * §4.4 says `set` is for. Mixing the two double-counts (trap 9), so `add` exists
 * here only to keep the wire contract honest, and nothing calls it today.
 */
export type OrgProgressOp = 'set' | 'add';

/** Backfill horizon for a self-reported value (§4.4 `too_old`). */
export const ORG_PROGRESS_BACKFILL_DAYS = 7;

/** Rejected, not clamped — so this is a pre-flight guard, never a silent fix. */
export function exceedsDailyCap(challenge: OrgChallenge, value: number): boolean {
  return challenge.maxDailyValue != null && value > challenge.maxDailyValue;
}

/** The caller's own leaderboard row, when their unit has participants. */
export function myLeaderboardRow(challenge: OrgChallenge): OrgLeaderboardRow | null {
  return challenge.leaderboard.find((row) => row.isMine) ?? null;
}

/**
 * How big one nudge of the self-report input is (§4.5 — "the step is yours to
 * choose; a cup of water, 0.25 L, is sensible").
 *
 * Keyed on `metric`, which §5 names as the STABLE identifier, never on
 * `unit_label` — that is display copy an admin can retitle, and it carries no
 * granularity. An unlisted metric falls back to 1: the counts the device-fed
 * metrics deal in (steps, minutes) are whole numbers, and a whole number is also
 * the safe answer for a metric this build has never heard of.
 *
 * This is a UI affordance only. It sets the spinner/arrow-key granularity; it is
 * NOT validation, and the server accepts any `numeric >= 0`, so a member who
 * types a value off the step is still submitting a value the server will take.
 */
export function reportStepFor(metric: string): number {
  return metric === 'water_liters' ? 0.25 : 1;
}

/**
 * Refusals that mean THIS TAB'S COPY OF THE DATA IS STALE, rather than that the
 * member did something wrong (§4.4's reason table):
 *
 *  · `not_yours` — the challenge belongs to another organization. The table's
 *    own instruction is "reload the tab".
 *  · `challenge_ended` — the challenge is over. The table notes `can_report`
 *    "would already have been false", i.e. the card on screen predates the end.
 *
 * In both cases the fix is the same and is not something to ask the member to
 * do: re-read the payload so the card they are looking at is the card the server
 * has. Every other reason is a genuine refusal of a specific value and must NOT
 * trigger a re-read — that would throw away the number they typed.
 */
export function reasonMeansStaleTab(reason: string | null): boolean {
  return reason === 'not_yours' || reason === 'challenge_ended';
}
